import { useEffect, useState } from "react";
import axios from "axios";

// Re-export the pure-function pattern helper so existing callers keep
// importing from "@/lib/redirects". Pattern + findRedirect live in a
// separate module so jest can test them without transforming axios.
export { findRedirect, PATTERN_REDIRECTS } from "./redirect-patterns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Module-scope redirect map — lazy-loaded once per page load. Used by
 * NotFoundPage to recover stale URLs without redeploying. The data comes
 * from `GET /api/redirects` (no auth) and lives in MongoDB so admins can
 * add/remove mappings via the /admin "404s" tab.
 *
 * We intentionally avoid persisting the map to localStorage — keeping it
 * memory-only means a fresh admin edit picks up on the next page load
 * rather than waiting for cache invalidation.
 */
let redirectsCache = null;
let redirectsPromise = null;

export function useRedirects() {
  const [redirects, setRedirects] = useState(redirectsCache);
  const [loading, setLoading] = useState(redirectsCache === null);

  useEffect(() => {
    if (redirectsCache !== null) {
      setRedirects(redirectsCache);
      setLoading(false);
      return;
    }
    if (!redirectsPromise) {
      redirectsPromise = axios
        .get(`${API}/redirects`, { timeout: 3000 })
        .then((r) => {
          redirectsCache = Array.isArray(r.data) ? r.data : [];
          return redirectsCache;
        })
        .catch(() => {
          // On error we don't want to block NotFoundPage rendering forever —
          // treat as "no redirects" and let the visitor see the 404.
          redirectsCache = [];
          return redirectsCache;
        });
    }
    let cancelled = false;
    redirectsPromise.then((d) => {
      if (cancelled) return;
      setRedirects(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  return { redirects: redirects || [], loading };
}
